import {
    useCallback,
    useLayoutEffect,
    useRef,
    useState
} from "react";
import {
    ActivityIndicator,
    FlatList,
    TouchableOpacity,
    View
} from "react-native";
import type {
    CampaignManagementObjectType
} from "./types";
import stylesheet from "./stylesheet";
import CampaignCard from "./components/campaignCard";
import {
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    NCoreUIKitTheme,
    PageContainer,
    BottomSheet,
    Dialog,
    Text,
    type IBottomSheetRef
} from "ncore-ui-kit";
import {
    useFocusEffect,
    useNavigation
} from "@react-navigation/native";
import {
    useSafeAreaInsets
} from "react-native-safe-area-context";
import type AppStackParamList from "../../../types/navigation/app";
import type {
    NativeStackNavigationProp
} from "@react-navigation/native-stack";
import {
    Check,
    ChevronLeft,
    CircleAlert,
    Pencil,
    Plus,
    Trash2
} from "lucide-react-native";
import {
    RESTService
} from "../../../services/restAPI";

const CampaignManagement = () => {
    const navigation = useNavigation<NativeStackNavigationProp<AppStackParamList>>();

    const {
        spaces,
        radiuses,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const inset = useSafeAreaInsets();

    const [relevantCampaigns,
        setRelevantCampaigns] = useState<Array<CampaignManagementObjectType>>([]);
    const [selecetedCampaign,
        setSelecetedCampaign] = useState<CampaignManagementObjectType | undefined>();
    const [successDialog,
        setSuccessDialog] = useState(false);
    const [verificationDialog,
        setVerificationDialog] = useState({
        isActive: false,
        campaignID: ""
    });
    const [loading,
        setLoading] = useState(true);

    const editRef = useRef<IBottomSheetRef>(null);

    useLayoutEffect(() => {
        navigation.setOptions({
            headerShown: true,
            header: () => <View
                style={[
                    stylesheet.headerContainer,
                    {
                        paddingTop: inset.top,
                        paddingHorizontal: spaces.spacingMd
                    }
                ]}
            >
                <TouchableOpacity
                    style={[
                        stylesheet.headerButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        navigation.goBack();
                    }}
                >
                    <ChevronLeft
                        size={30}
                    />
                </TouchableOpacity>
                <Text
                    variant="titleLargeSize"
                >
                    {localize("campaignManagement")}
                </Text>
                <TouchableOpacity
                    style={[
                        stylesheet.headerButton,
                        {
                            padding: spaces.spacingSm
                        }
                    ]}
                    onPress={() => {
                        const activeCompanies = relevantCampaigns.map(e => e.companyID);

                        navigation.navigate("CreateCampaign", {
                            activeCompanies: activeCompanies
                        });
                    }}
                >
                    <Plus/>
                </TouchableOpacity>
            </View>
        });
    }, [navigation]);


    useFocusEffect(
        useCallback(() => {
            getCampaigns();
        }, [])
    );

    const getCampaigns = () => {
        RESTService.action("GetMyOpportunities", null)
            .then((res) => {
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    const myActiveOpportunities: Array<CampaignManagementObjectType> = res.payload.myActiveOpportunities.map(e => {
                        return {
                            ...e,
                            isApproved: true
                        };
                    });

                    const myOpportunityRequests: Array<CampaignManagementObjectType> = res.payload.myOpportunityRequests.map(e => {
                        return {
                            ...e,
                            isApproved: false
                        };
                    });

                    setRelevantCampaigns([
                        ...myActiveOpportunities,
                        ...myOpportunityRequests
                    ]);

                    setLoading(false);
                }
            }).catch((err) => {
                console.log("err", err);
            });
    };

    const removeCampaign = (campaignID: string) => {
        RESTService.action("RemoveCommercialOpportunity", {
            opportunityID: campaignID
        })
            .then((res) => {
                setVerificationDialog({
                    isActive: false,
                    campaignID: ""
                });
                if(res.code === 200 || res.code === 201 || res.code === 202) {
                    const _relevantCampaigns: Array<CampaignManagementObjectType> = JSON.parse(JSON.stringify(relevantCampaigns));

                    const _index = _relevantCampaigns.findIndex(e => e._id === campaignID);

                    if(_index !== -1) {
                        _relevantCampaigns.splice(_index, 1);
                        setRelevantCampaigns(_relevantCampaigns);
                        setSuccessDialog(true);
                    } else {
                        NCoreUIKitSnackBar.open({
                            title: localize("campaignNotFound"),
                            type: "danger",
                            isShowAction: false,
                            autoCloseDelay: 10000,
                            icon: ({
                                color
                            }) => <CircleAlert
                                color={colors.content.icon[color ? color : "default"]}
                            />
                        });
                    }

                }
            }).catch((err) => {
                console.log("err", err);
            });
    };

    const renderEditSheetContent = () => {
        if(selecetedCampaign) {
            return  <View
                style={{
                    backgroundColor: colors.content.container.default,
                    borderRadius: radiuses.md
                }}
            >
                {
                    selecetedCampaign.isApproved ? null : <TouchableOpacity
                        onPress={() => {
                            navigation.navigate("EditCampaign", {
                                campaignDetail: selecetedCampaign
                            });
                            editRef.current?.close();
                        }}
                        style={stylesheet.actionItem}
                    >
                        <View
                            style={[
                                stylesheet.actionIconContainer,
                                {
                                    padding: spaces.spacingMd,
                                    backgroundColor: colors.content.container.subtle,
                                    borderRadius: radiuses.form,
                                    marginRight: spaces.spacingMd
                                }
                            ]}
                        >
                            <Pencil
                                color={colors.content.icon.warning}
                            />
                        </View>
                        <Text
                            variant="titleMediumSize"
                        >
                            {localize("edit")}
                        </Text>
                    </TouchableOpacity>
                }
                <TouchableOpacity
                    style={stylesheet.actionItem}
                    onPress={() => {
                        setVerificationDialog({
                            isActive: true,
                            campaignID: selecetedCampaign._id
                        });
                        editRef.current?.close();
                    }}
                >
                    <View
                        style={[
                            stylesheet.actionIconContainer,
                            {
                                padding: spaces.spacingMd,
                                backgroundColor: colors.content.container.subtle,
                                borderRadius: radiuses.form,
                                marginRight: spaces.spacingMd
                            }
                        ]}
                    >
                        <Trash2
                            color={colors.content.icon.danger}
                        />
                    </View>
                    <Text
                        variant="titleMediumSize"
                    >
                        {localize("delete")}
                    </Text>
                </TouchableOpacity>
            </View>;
        } else {
            return;
        }
    };


    const editSheet = () => {
        return <BottomSheet
            ref={editRef}
            isAutoHeight={true}
            style={stylesheet.bottomSheet}
            backgroundColor="subtle"
            scrollViewProps={{
                contentContainerStyle: {
                    flex: 1
                }
            }}
            onClose={() => {
                setSelecetedCampaign(undefined);
            }}
            renderHeader={() => <View
                style={[
                    stylesheet.bottomSheetHeader,
                    {
                        paddingBottom: spaces.spacingXl,
                        paddingHorizontal: spaces.spacingXl,
                        paddingTop: spaces.spacingMd
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("editCampaign")}
                </Text>
            </View>}
        >
            {renderEditSheetContent()}
        </BottomSheet>;
    };

    const renderSuccessDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.dialogHeader}
            >
                <View
                    style={[
                        stylesheet.successIconContainer,
                        {
                            backgroundColor: colors.content.container.success,
                            padding: spaces.spacingMd,
                            marginBottom: spaces.spacingLg,
                            borderRadius: radiuses.full
                        }
                    ]}
                >
                    <Check
                        size={30}
                        color={colors.content.icon.success}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                >
                    {localize("submittedSuccessfully")}
                </Text>
            </View>}
            content={localize("campaignRemoved")}
            isVisible={successDialog}
            onOverlayPress={() => {
                setSuccessDialog(false);
            }}
            primaryButtonProps={{
                onPress: () => {
                    setSuccessDialog(false);
                },
                title: localize("ok"),
                type: "success"
            }}
            variant="ok"
        />;
    };

    const renderVerificationDialog = () => {
        return <Dialog
            headerComponent={<View
                style={stylesheet.dialogHeader}
            >
                <View
                    style={[
                        stylesheet.dangerIconContainer,
                        {
                            marginBottom: spaces.spacingLg,
                            backgroundColor: colors.content.container.warning,
                            padding: spaces.spacingMd,
                            borderRadius: radiuses.full
                        }
                    ]}
                >
                    <Trash2
                        size={40}
                        color={colors.content.icon.warning}
                    />
                </View>
                <Text
                    variant="titleLargeSize"
                    color="high"
                    style={stylesheet.textCenter}
                >
                    {localize("removeCampaignConfirm")}
                </Text>
            </View>}
            content={localize("submitForApprovalDescription")}
            isVisible={verificationDialog.isActive}
            onOverlayPress={() => {
                setVerificationDialog({
                    campaignID: "",
                    isActive: false
                });
            }}
            primaryButtonProps={{
                onPress: () => {
                    if(verificationDialog.campaignID && verificationDialog.campaignID.length) {
                        removeCampaign(verificationDialog.campaignID);
                    }
                },
                type: "warning",
                title: localize("remove")
            }}
            secondaryButtonProps={{
                title: localize("abandon"),
                onPress: () => {
                    setVerificationDialog({
                        campaignID: "",
                        isActive: false
                    });
                }
            }}
            variant="yes-no"
        />;
    };

    if(loading) {
        return <PageContainer>
            <ActivityIndicator
                size={"large"}
                color={colors.content.icon.default}
            />
        </PageContainer>;
    }

    return <PageContainer
        backgroundColor="default"
        isScrollable={false}
    >
        <FlatList
            showsVerticalScrollIndicator={false}
            keyExtractor={(item) => item._id}
            renderItem={({
                item
            }) => {
                return <CampaignCard
                    key={item._id}
                    campaignDesc={item.description}
                    campaignTitle={item.title}
                    campaignLogo={item.coverPhotoURL}
                    companyTitle={item.companyTitle}
                    isApproved={item.isApproved}
                    onPress={() => {
                        setSelecetedCampaign(item);
                        editRef.current?.open();
                    }}
                />;
            }}
            horizontal={false}
            data={relevantCampaigns}
        />
        {editSheet()}
        {renderSuccessDialog()}
        {renderVerificationDialog()}
    </PageContainer>;
};
export default CampaignManagement;
